/**
 * Design JSON V2 — schema validator.
 *
 * Deterministic, side-effect-free structural validation of a Design JSON V2
 * document against the frozen contract. It NEVER throws on malformed input
 * (null, wrong types, garbage) and NEVER mutates the input — it only reports.
 *
 * Severity model (matches the frozen architecture):
 *   ERROR   — the compiler could not produce a valid template from this
 *             (unknown node/fill/effect/mask kind, non-finite present geometry,
 *             missing component reference, no pages, wrong schema). valid=false.
 *   WARNING — recoverable via downstream defaults (unknown section role [the
 *             role vocabulary is intentionally open], unknown blend/align mode,
 *             out-of-range opacity, empty page/section, unresolved token ref).
 *
 * Repair/coercion is intentionally NOT done here — this module only decides
 * validity and surfaces actionable findings.
 */

import {
  SCHEMA_ID, SCHEMA_VERSION, NODE_KINDS, PRIMITIVE_TYPES, SHAPE_KINDS,
  FILL_KINDS, EFFECT_KINDS, MASK_KINDS, LAYOUT_TYPES, SIZING_MODES,
  ALIGN_MODES, DISTRIBUTE_MODES, SECTION_ROLES, LAYER_BANDS, BLEND_MODES,
  STROKE_ALIGNS, STROKE_STYLES, CONSTRAINT_H, CONSTRAINT_V, ORIENTATIONS, UNITS, FIT_MODES,
} from '../schema/constants';

export const SEVERITY = Object.freeze({ ERROR: 'error', WARNING: 'warning' });

const MAX_DEPTH = 32; // defensive guard against pathologically nested input

// ── type guards ──────────────────────────────────────────────────────────────
const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isArr = Array.isArray;
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const isStr = (v) => typeof v === 'string';
const isBool = (v) => typeof v === 'boolean';
const isHex = (v) => isStr(v) && /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v);

/**
 * A collector that accumulates findings without throwing.
 */
class Findings {
  constructor() { this.errors = []; this.warnings = []; }
  error(code, path, message) { this.errors.push({ severity: SEVERITY.ERROR, code, path, message }); }
  warn(code, path, message) { this.warnings.push({ severity: SEVERITY.WARNING, code, path, message }); }
}

// ── leaf validators ──────────────────────────────────────────────────────────

const validateGeometry = (n, path, f) => {
  // Geometry is OPTIONAL (intent language). Only validate when present.
  [['x', n.x], ['y', n.y], ['width', n.width], ['height', n.height]].forEach(([k, v]) => {
    if (v !== null && v !== undefined && !isNum(v)) {
      f.error('geometry-type', `${path}.${k}`, `"${k}" must be a finite number when present`);
    }
  });
  if (isNum(n.width) && n.width < 0) f.error('geometry-range', `${path}.width`, 'width must be >= 0');
  if (isNum(n.height) && n.height < 0) f.error('geometry-range', `${path}.height`, 'height must be >= 0');
  if (n.rotation !== undefined && !isNum(n.rotation)) f.warn('rotation-type', `${path}.rotation`, 'rotation should be a number');
};

const validateFills = (fills, path, f) => {
  if (fills === undefined) return;
  if (!isArr(fills)) { f.error('fills-type', path, 'fills must be an array'); return; }
  fills.forEach((fill, i) => {
    const p = `${path}[${i}]`;
    if (!isObj(fill)) { f.error('fill-type', p, 'fill must be an object'); return; }
    if (!FILL_KINDS.includes(fill.kind)) f.error('fill-kind', `${p}.kind`, `unknown fill kind "${fill.kind}"`);
    if (fill.opacity !== undefined && (!isNum(fill.opacity) || fill.opacity < 0 || fill.opacity > 1)) {
      f.warn('fill-opacity', `${p}.opacity`, 'opacity should be a number in [0,1]');
    }
    if (fill.blend !== undefined && !BLEND_MODES.includes(fill.blend)) {
      f.warn('fill-blend', `${p}.blend`, `unknown blend mode "${fill.blend}"`);
    }
  });
};

const validateStroke = (stroke, path, f) => {
  if (stroke === undefined) return;
  if (!isObj(stroke)) { f.error('stroke-type', path, 'stroke must be an object'); return; }
  if (stroke.width !== undefined && (!isNum(stroke.width) || stroke.width < 0)) {
    f.error('stroke-width', `${path}.width`, 'stroke width must be a number >= 0');
  }
  if (stroke.align !== undefined && !STROKE_ALIGNS.includes(stroke.align)) f.warn('stroke-align', `${path}.align`, `unknown stroke align "${stroke.align}"`);
  if (stroke.style !== undefined && !STROKE_STYLES.includes(stroke.style)) f.warn('stroke-style', `${path}.style`, `unknown stroke style "${stroke.style}"`);
};

const validateEffects = (effects, path, f) => {
  if (effects === undefined) return;
  if (!isArr(effects)) { f.error('effects-type', path, 'effects must be an array'); return; }
  effects.forEach((e, i) => {
    const p = `${path}[${i}]`;
    if (!isObj(e)) { f.error('effect-type', p, 'effect must be an object'); return; }
    if (!EFFECT_KINDS.includes(e.kind)) f.error('effect-kind', `${p}.kind`, `unknown effect kind "${e.kind}"`);
  });
};

const validateMask = (mask, path, f) => {
  if (mask === undefined || mask === null) return;
  if (!isObj(mask)) { f.error('mask-type', path, 'mask must be an object'); return; }
  if (!MASK_KINDS.includes(mask.kind)) f.error('mask-kind', `${path}.kind`, `unknown mask kind "${mask.kind}"`);
};

const validateConstraints = (c, path, f) => {
  if (c === undefined) return;
  if (!isObj(c)) { f.warn('constraints-type', path, 'constraints should be an object'); return; }
  if (c.h !== undefined && !CONSTRAINT_H.includes(c.h)) f.warn('constraint-h', `${path}.h`, `unknown horizontal constraint "${c.h}"`);
  if (c.v !== undefined && !CONSTRAINT_V.includes(c.v)) f.warn('constraint-v', `${path}.v`, `unknown vertical constraint "${c.v}"`);
};

const validateEditable = (e, path, f) => {
  if (e === undefined) return;
  if (!isObj(e)) { f.warn('editable-type', path, 'editable should be an object'); return; }
  ['content', 'style', 'position', 'size', 'swappable', 'locked'].forEach((k) => {
    if (e[k] !== undefined && !isBool(e[k])) f.warn('editable-flag', `${path}.${k}`, `editable.${k} should be a boolean`);
  });
};

const validateAutoLayout = (layout, path, f) => {
  if (layout === undefined) return;
  if (!isObj(layout)) { f.warn('layout-type', path, 'layout should be an object'); return; }
  if (layout.type !== undefined && !LAYOUT_TYPES.includes(layout.type)) f.warn('layout-kind', `${path}.type`, `unknown layout type "${layout.type}"`);
  if (layout.align !== undefined && !ALIGN_MODES.includes(layout.align)) f.warn('layout-align', `${path}.align`, `unknown align "${layout.align}"`);
  if (layout.distribute !== undefined && !DISTRIBUTE_MODES.includes(layout.distribute)) f.warn('layout-distribute', `${path}.distribute`, `unknown distribute "${layout.distribute}"`);
  if (layout.sizing !== undefined && !SIZING_MODES.includes(layout.sizing)) f.warn('layout-sizing', `${path}.sizing`, `unknown sizing "${layout.sizing}"`);
};

/** A color value may be a hex, or a reference to a defined color token. */
const validateColorRef = (value, path, tokens, f) => {
  if (value === undefined || value === null) return; // unset → compiler/genome fills
  if (isHex(value)) return;
  if (isStr(value) && tokens.color && value in tokens.color) return;
  f.warn('token-color-ref', path, `color "${value}" is neither a hex nor a defined color token`);
};

const validateTypeRef = (value, path, tokens, f) => {
  if (value === undefined || value === null) return;
  if (isStr(value) && tokens.type && value in tokens.type) return;
  f.warn('token-type-ref', path, `type style "${value}" is not a defined type token`);
};

// ── node validation (recursive) ──────────────────────────────────────────────

const validateObjectNode = (n, path, tokens, f) => {
  if (!PRIMITIVE_TYPES.includes(n.type)) { f.error('object-type', `${path}.type`, `unknown object type "${n.type}"`); return; }
  switch (n.type) {
    case 'text':
      if (n.text !== undefined && !isStr(n.text)) f.error('text-content', `${path}.text`, 'text must be a string');
      if (n.text === undefined || n.text === '') f.warn('text-empty', `${path}.text`, 'text node has no content');
      validateTypeRef(n.style, `${path}.style`, tokens, f);
      validateColorRef(n.color, `${path}.color`, tokens, f);
      break;
    case 'shape':
      if (n.shape !== undefined && !SHAPE_KINDS.includes(n.shape)) f.warn('shape-kind', `${path}.shape`, `unknown shape "${n.shape}"`);
      if (n.cornerRadius !== undefined && !isNum(n.cornerRadius)) f.warn('shape-radius', `${path}.cornerRadius`, 'cornerRadius should be a number');
      break;
    case 'image':
      if ((n.query === undefined || n.query === null) && (n.src === undefined || n.src === null)) {
        f.warn('image-source', path, 'image has neither a query nor a src');
      }
      if (n.fit !== undefined && !FIT_MODES.includes(n.fit)) f.warn('image-fit', `${path}.fit`, `unknown fit "${n.fit}"`);
      break;
    case 'icon':
      if (n.icon === undefined || n.icon === null) f.warn('icon-name', path, 'icon has no name');
      validateColorRef(n.color, `${path}.color`, tokens, f);
      break;
    case 'vector':
      if ((n.path === undefined || n.path === null) && (n.ref === undefined || n.ref === null)) {
        f.warn('vector-source', path, 'vector has neither a path nor a ref');
      }
      break;
    case 'line':
      if (n.thickness !== undefined && !isNum(n.thickness)) f.warn('line-thickness', `${path}.thickness`, 'thickness should be a number');
      break;
    default: break;
  }
};

const validateNode = (n, path, tokens, f, depth) => {
  if (depth > MAX_DEPTH) { f.error('nesting', path, 'node nesting exceeds the maximum depth'); return; }
  if (!isObj(n)) { f.error('node-type', path, 'node must be an object'); return; }
  if (!NODE_KINDS.includes(n.node)) { f.error('node-kind', `${path}.node`, `unknown node kind "${n.node}"`); return; }
  if (n.layer !== undefined && !LAYER_BANDS.includes(n.layer)) f.warn('node-layer', `${path}.layer`, `unknown layer band "${n.layer}"`);

  validateGeometry(n, path, f);
  validateFills(n.fills, `${path}.fills`, f);
  validateStroke(n.stroke, `${path}.stroke`, f);
  validateEffects(n.effects, `${path}.effects`, f);
  validateMask(n.mask, `${path}.mask`, f);
  validateConstraints(n.constraints, `${path}.constraints`, f);
  validateEditable(n.editable, `${path}.editable`, f);

  switch (n.node) {
    case 'object':
      validateObjectNode(n, path, tokens, f);
      break;
    case 'group':
      validateAutoLayout(n.layout, `${path}.layout`, f);
      if (n.children !== undefined) {
        if (!isArr(n.children)) f.error('group-children', `${path}.children`, 'group children must be an array');
        else n.children.forEach((c, i) => validateNode(c, `${path}.children[${i}]`, tokens, f, depth + 1));
      }
      break;
    case 'frame':
      if (n.child !== undefined && n.child !== null) validateNode(n.child, `${path}.child`, tokens, f, depth + 1);
      break;
    case 'component':
      if (n.component === undefined || n.component === null) f.error('component-ref', path, 'component instance is missing its component reference');
      if (n.props !== undefined && !isObj(n.props)) f.warn('component-props', `${path}.props`, 'component props should be an object');
      if (n.slots !== undefined && !isObj(n.slots)) f.warn('component-slots', `${path}.slots`, 'component slots should be an object');
      break;
    default: break;
  }
};

// ── structure validation ─────────────────────────────────────────────────────

const validateTokens = (tokens, f) => {
  if (!isObj(tokens)) { f.warn('tokens-type', 'tokens', 'tokens should be an object'); return {}; }
  ['color', 'type', 'space'].forEach((g) => {
    if (tokens[g] !== undefined && !isObj(tokens[g])) f.warn('token-group', `tokens.${g}`, `tokens.${g} should be an object`);
  });
  return tokens;
};

const validateGrid = (grid, path, f) => {
  if (grid === undefined) return;
  if (!isObj(grid)) { f.warn('grid-type', path, 'grid should be an object'); return; }
  if (grid.cols !== undefined && (!isNum(grid.cols) || grid.cols < 1)) f.warn('grid-cols', `${path}.cols`, 'grid.cols should be a number >= 1');
};

const validateCanvas = (canvas, path, f) => {
  if (!isObj(canvas)) { f.error('canvas-type', path, 'canvas must be an object'); return; }
  if (!isNum(canvas.width) || canvas.width <= 0) f.error('canvas-width', `${path}.width`, 'canvas.width must be a positive number');
  if (!isNum(canvas.height) || canvas.height <= 0) f.error('canvas-height', `${path}.height`, 'canvas.height must be a positive number');
  if (canvas.unit !== undefined && !UNITS.includes(canvas.unit)) f.warn('canvas-unit', `${path}.unit`, `unknown unit "${canvas.unit}"`);
  if (canvas.orientation !== undefined && !ORIENTATIONS.includes(canvas.orientation)) f.warn('canvas-orientation', `${path}.orientation`, `unknown orientation "${canvas.orientation}"`);
  if (canvas.safeArea !== undefined && (!isNum(canvas.safeArea) || canvas.safeArea < 0 || canvas.safeArea >= 0.5)) {
    f.warn('canvas-safearea', `${path}.safeArea`, 'safeArea should be a fraction in [0,0.5)');
  }
  validateGrid(canvas.grid, `${path}.grid`, f);
};

const validateSection = (s, path, tokens, f) => {
  if (!isObj(s)) { f.error('section-type', path, 'section must be an object'); return; }
  if (s.role !== undefined && !SECTION_ROLES.includes(s.role)) {
    // The role vocabulary is intentionally OPEN — unknown roles are tolerated.
    f.warn('section-role', `${path}.role`, `"${s.role}" is not a standard section role (allowed, compiled as generic content)`);
  }
  validateAutoLayout(s.layout, `${path}.layout`, f);
  if (!isArr(s.nodes)) { f.error('section-nodes', `${path}.nodes`, 'section.nodes must be an array'); return; }
  if (s.nodes.length === 0) f.warn('section-empty', path, 'section has no nodes');
  s.nodes.forEach((n, i) => validateNode(n, `${path}.nodes[${i}]`, tokens, f, 0));
};

const validatePage = (page, path, tokens, f) => {
  if (!isObj(page)) { f.error('page-type', path, 'page must be an object'); return; }
  validateCanvas(page.canvas, `${path}.canvas`, f);
  if (page.background !== undefined && !isArr(page.background)) f.error('page-background', `${path}.background`, 'page.background must be an array (layered fill stack)');
  else if (isArr(page.background)) validateFills(page.background, `${path}.background`, f);
  if (page.layers !== undefined && !isArr(page.layers)) f.warn('page-layers', `${path}.layers`, 'page.layers should be an array');
  if (!isArr(page.sections)) { f.error('page-sections', `${path}.sections`, 'page.sections must be an array'); return; }
  if (page.sections.length === 0) f.warn('page-empty', path, 'page has no sections');
  page.sections.forEach((s, i) => validateSection(s, `${path}.sections[${i}]`, tokens, f));
};

const validateVariant = (v, path, f) => {
  if (!isObj(v)) { f.warn('variant-type', path, 'variant should be an object'); return; }
  if (v.size !== undefined && v.size !== null && !isStr(v.size)) f.warn('variant-size', `${path}.size`, 'variant.size should be a string');
  if (v.reflow !== undefined && !['constraints', 'reflow'].includes(v.reflow)) f.warn('variant-reflow', `${path}.reflow`, `unknown reflow "${v.reflow}"`);
};

/**
 * Validate a Design JSON V2 document.
 * @param {*} doc
 * @returns {{ valid: boolean, errors: Object[], warnings: Object[] }}
 */
export const validateDesignDocument = (doc) => {
  const f = new Findings();

  if (!isObj(doc)) {
    f.error('document-type', '', 'document must be an object');
    return { valid: false, errors: f.errors, warnings: f.warnings };
  }

  if (doc.schema !== SCHEMA_ID) f.error('schema-id', 'schema', `schema must be "${SCHEMA_ID}"`);
  if (doc.version !== undefined && doc.version !== SCHEMA_VERSION) {
    f.warn('schema-version', 'version', `version ${doc.version} differs from ${SCHEMA_VERSION}`);
  }
  if (doc.meta !== undefined && !isObj(doc.meta)) f.warn('meta-type', 'meta', 'meta should be an object');
  if (doc.components !== undefined && !isArr(doc.components)) f.error('components-type', 'components', 'components must be an array');
  if (doc.variants !== undefined) {
    if (!isArr(doc.variants)) f.error('variants-type', 'variants', 'variants must be an array');
    else doc.variants.forEach((v, i) => validateVariant(v, `variants[${i}]`, f));
  }

  const tokens = validateTokens(doc.tokens, f);

  if (!isArr(doc.pages) || doc.pages.length === 0) {
    f.error('pages', 'pages', 'document must have a non-empty pages array');
  } else {
    doc.pages.forEach((p, i) => validatePage(p, `pages[${i}]`, tokens, f));
  }

  return { valid: f.errors.length === 0, errors: f.errors, warnings: f.warnings };
};
